import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { LogOut, RefreshCw, Trash2, Loader2, Inbox, ImagePlus, Images } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { photoSrc } from "@/components/Gallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["new", "contacted", "done"];

const statusStyle = {
  new: "bg-sky-500/15 text-sky-400 border-sky-500/40",
  contacted: "bg-cyan-500/15 text-cyan-400 border-cyan-500/40",
  done: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
};

const fmtErr = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return "Something went wrong";
};

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onLogin(data);
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
      <div className="rounded-xl border border-white/10 bg-carbon p-8 sm:p-10 shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
        <Logo />
        <h1 className="mt-8 font-display font-bold uppercase text-3xl text-gray-50" data-testid="admin-login-heading">Admin Access</h1>
        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gray-500">Enquiries dashboard</p>
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <input
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full bg-obsidian border border-white/15 rounded-md px-4 py-3.5 text-sm text-gray-100 placeholder:text-gray-600 focus:outline-none focus:border-sky-500/70 transition-colors"
          />
          <input
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full bg-obsidian border border-white/15 rounded-md px-4 py-3.5 text-sm text-gray-100 placeholder:text-gray-600 focus:outline-none focus:border-sky-500/70 transition-colors"
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-sky-500 text-obsidian font-display font-bold uppercase tracking-widest text-sm py-3.5 rounded-md hover:bg-sky-400 disabled:opacity-60 transition-colors"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Sign In
          </button>
        </form>
      </div>
    </motion.div>
  );
};

const EnquiryCard = ({ enquiry, onStatus, onDelete }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0 }}
    data-testid={`enquiry-card-${enquiry.reference}`}
    className="rounded-xl border border-white/10 bg-carbon p-5 sm:p-6"
  >
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-display font-bold uppercase text-xl text-gray-50">{enquiry.name}</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sky-500/90 mt-1">
          {enquiry.reference} · {new Date(enquiry.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}
        </p>
      </div>
      <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border ${statusStyle[enquiry.status] || statusStyle.new}`} data-testid={`enquiry-status-${enquiry.reference}`}>
        {enquiry.status}
      </span>
    </div>
    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-gray-400">
      <p><span className="text-gray-600">Phone:</span> <a href={`tel:${enquiry.phone}`} className="text-gray-200 hover:text-sky-400">{enquiry.phone}</a></p>
      <p><span className="text-gray-600">Email:</span> <a href={`mailto:${enquiry.email}`} className="text-gray-200 hover:text-sky-400">{enquiry.email}</a></p>
      <p><span className="text-gray-600">Vehicle:</span> <span className="text-gray-200">{enquiry.vehicle}</span></p>
      <p><span className="text-gray-600">Service:</span> <span className="text-gray-200">{enquiry.service}</span></p>
    </div>
    {enquiry.message && (
      <p className="mt-3 text-sm text-gray-400 border-l-2 border-sky-500/40 pl-3">{enquiry.message}</p>
    )}
    <div className="mt-5 flex flex-wrap items-center gap-2">
      {STATUSES.map((s) => (
        <button
          key={s}
          data-testid={`enquiry-mark-${s}-${enquiry.reference}`}
          onClick={() => onStatus(enquiry.id, s)}
          className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 rounded-md border transition-colors ${
            enquiry.status === s
              ? "bg-sky-500 text-obsidian border-sky-500"
              : "border-white/15 text-gray-400 hover:border-sky-500/50 hover:text-sky-400"
          }`}
        >
          {s}
        </button>
      ))}
      <button
        data-testid={`enquiry-delete-${enquiry.reference}`}
        onClick={() => onDelete(enquiry.id, enquiry.reference)}
        className="ml-auto p-2 text-gray-500 hover:text-red-400 transition-colors"
        aria-label="Delete enquiry"
      >
        <Trash2 size={17} />
      </button>
    </div>
  </motion.div>
);

const GalleryManager = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("");
  const [cat, setCat] = useState("installs");
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/gallery`);
      setPhotos(data);
    } catch {
      toast.error("Could not load gallery photos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose a photo first");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("label", label);
      form.append("cat", cat);
      await axios.post(`${API}/admin/gallery`, form, { withCredentials: true });
      toast.success("Photo added to gallery");
      setFile(null);
      setLabel("");
      setCat("installs");
      if (fileInput.current) fileInput.current.value = "";
      await load();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id, photoLabel) => {
    if (!window.confirm(`Remove "${photoLabel}" from the gallery?`)) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { withCredentials: true });
      setPhotos((list) => list.filter((p) => p.id !== id));
      toast.success("Photo removed");
    } catch {
      toast.error("Could not remove photo");
    }
  };

  const inputCls =
    "w-full bg-obsidian border border-white/15 rounded-md px-4 py-3 text-sm text-gray-100 placeholder:text-gray-600 focus:outline-none focus:border-sky-500/70 transition-colors";

  return (
    <div data-testid="gallery-manager">
      <form onSubmit={upload} className="rounded-xl border border-white/10 bg-carbon p-5 sm:p-6 mb-8" data-testid="gallery-upload-form">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sky-500/90 mb-4">Add a photo</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            ref={fileInput}
            data-testid="gallery-upload-file"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full bg-obsidian border border-white/15 rounded-md px-4 py-2.5 text-sm text-gray-400 file:mr-4 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-sky-500 file:text-obsidian file:text-xs file:font-bold file:uppercase hover:file:bg-sky-400 file:cursor-pointer"
          />
          <input
            data-testid="gallery-upload-label"
            type="text"
            required
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Caption, e.g. BlackVue fitment — Mazda CX-5"
            className={inputCls}
          />
          <select data-testid="gallery-upload-cat" value={cat} onChange={(e) => setCat(e.target.value)} className={inputCls}>
            <option value="installs">Installs</option>
            <option value="detail">Detail</option>
          </select>
          <button
            data-testid="gallery-upload-submit"
            type="submit"
            disabled={uploading}
            className="flex items-center justify-center gap-2 bg-sky-500 text-obsidian font-display font-bold uppercase tracking-widest text-sm py-3 rounded-md hover:bg-sky-400 disabled:opacity-60 transition-colors"
          >
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
            {uploading ? "Uploading..." : "Upload Photo"}
          </button>
        </div>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-gray-600">JPG, PNG or WEBP — max 10MB. Shows on the site instantly.</p>
      </form>

      {loading ? (
        <div className="flex justify-center py-16" data-testid="gallery-manager-loading">
          <Loader2 size={26} className="animate-spin text-sky-500" />
        </div>
      ) : photos.length === 0 ? (
        <div className="flex flex-col items-center py-16 text-center rounded-xl border border-white/10 bg-carbon" data-testid="gallery-manager-empty">
          <Images size={36} className="text-gray-600" />
          <p className="mt-4 font-display font-bold uppercase text-xl text-gray-400">No photos yet</p>
          <p className="mt-2 text-sm text-gray-600">Upload your first install photo above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-manager-grid">
          {photos.map((p) => (
            <div key={p.id} data-testid={`gallery-photo-${p.id}`} className="group relative rounded-lg overflow-hidden border border-white/10 bg-carbon">
              <img src={photoSrc(p)} alt={p.label} loading="lazy" className="w-full aspect-[4/3] object-cover" />
              <div className="p-3 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-xs text-gray-200 truncate">{p.label}</p>
                  <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-sky-500/80 mt-1">{p.cat}</p>
                </div>
                <button
                  data-testid={`gallery-photo-delete-${p.id}`}
                  onClick={() => remove(p.id, p.label)}
                  className="shrink-0 p-1.5 text-gray-500 hover:text-red-400 transition-colors"
                  aria-label={`Delete ${p.label}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, { withCredentials: true });
      setEnquiries(data);
    } catch {
      toast.error("Could not load enquiries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { status }, { withCredentials: true });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async (id, reference) => {
    if (!window.confirm(`Delete enquiry ${reference}?`)) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${id}`, { withCredentials: true });
      setEnquiries((list) => list.filter((e) => e.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Could not delete enquiry");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    onLogout();
  };

  const visible = filter === "all" ? enquiries : enquiries.filter((e) => e.status === filter);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: enquiries.filter((e) => e.status === s).length }), {});
  const newCount = counts.new || 0;

  return (
    <div className="w-full max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display font-bold uppercase text-3xl sm:text-4xl text-gray-50" data-testid="admin-dashboard-heading">Dashboard</h1>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gray-500 mt-2">
            Signed in as {user.email}
          </p>
        </div>
        <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 bg-sky-500 text-obsidian font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 rounded-md hover:bg-sky-400 transition-colors">
          <LogOut size={14} /> Logout
        </button>
      </div>

      <div className="flex gap-2 mb-8 border-b border-white/10 pb-4" data-testid="admin-tabs">
        <button
          data-testid="admin-tab-enquiries"
          onClick={() => setTab("enquiries")}
          className={`font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-md border transition-colors ${
            tab === "enquiries" ? "bg-sky-500 text-obsidian border-sky-500" : "border-white/15 text-gray-400 hover:border-sky-500/50 hover:text-sky-400"
          }`}
        >
          Enquiries {newCount > 0 && `(${newCount} new)`}
        </button>
        <button
          data-testid="admin-tab-gallery"
          onClick={() => setTab("gallery")}
          className={`font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-md border transition-colors ${
            tab === "gallery" ? "bg-sky-500 text-obsidian border-sky-500" : "border-white/15 text-gray-400 hover:border-sky-500/50 hover:text-sky-400"
          }`}
        >
          Gallery Photos
        </button>
      </div>

      {tab === "gallery" ? (
        <GalleryManager />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="admin-filters">
            {["all", ...STATUSES].map((f) => (
              <button
                key={f}
                data-testid={`admin-filter-${f}`}
                onClick={() => setFilter(f)}
                className={`font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 rounded-full border transition-colors ${
                  filter === f ? "bg-sky-500 text-obsidian border-sky-500" : "border-white/15 text-gray-400 hover:border-sky-500/50 hover:text-sky-400"
                }`}
              >
                {f} {f === "all" ? `(${enquiries.length})` : `(${counts[f] || 0})`}
              </button>
            ))}
            <button data-testid="admin-refresh-btn" onClick={load} className="ml-auto flex items-center gap-2 border border-white/15 text-gray-300 font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 rounded-full hover:border-sky-500/50 hover:text-sky-400 transition-colors">
              <RefreshCw size={13} /> Refresh
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-20" data-testid="admin-loading">
              <Loader2 size={28} className="animate-spin text-sky-500" />
            </div>
          ) : visible.length === 0 ? (
            <div className="flex flex-col items-center py-20 text-center rounded-xl border border-white/10 bg-carbon" data-testid="admin-empty">
              <Inbox size={40} className="text-gray-600" />
              <p className="mt-4 font-display font-bold uppercase text-xl text-gray-400">No enquiries yet</p>
              <p className="mt-2 text-sm text-gray-600">New submissions from the website will appear here.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="admin-enquiries-list">
              {visible.map((e) => (
                <EnquiryCard key={e.id} enquiry={e} onStatus={updateStatus} onDelete={remove} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  return (
    <main data-testid="admin-portal" className="grain min-h-screen bg-obsidian text-gray-100 font-sans flex items-start sm:items-center justify-center px-5 py-16">
      <div className="absolute inset-0 bg-[radial-gradient(#1f2937_1px,transparent_1px)] [background-size:26px_26px] opacity-20 pointer-events-none" />
      {user === undefined ? (
        <Loader2 size={28} className="animate-spin text-sky-500 mt-40" data-testid="admin-session-loading" />
      ) : user ? (
        <Dashboard user={user} onLogout={() => setUser(null)} />
      ) : (
        <LoginForm onLogin={setUser} />
      )}
    </main>
  );
}
